import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useProfiles } from './ProfileContext';
import { useModal } from './ModalContext';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { Modal, ModalHeader, ModalBody, Row, Col } from 'reactstrap';

const AstroAvatarsContext = createContext();

export const useAstroAvatars = () => {
    return useContext(AstroAvatarsContext);
};

export const AstroAvatarsProvider = ({ children }) => {
    const [astroAvatars, setAstroAvatars] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedAvatar, setSelectedAvatar] = useState(null);
    const [profileModal, setProfileModal] = useState(false);
    const [profileTitle, setProfileTitle] = useState('');
    const [profileDescription, setProfileDescription] = useState('');
    const navigate = useNavigate();
    const { profiles } = useProfiles();
    const { toggleAddProfile } = useModal();
    const apiUrl = process.env.REACT_APP_API_URL;
    const token = localStorage.getItem('token');

    useEffect(() => {
        const fetchAstroAvatars = async () => {
            try {
                const response = await axios.get(`${apiUrl}/astro-avatar`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                setAstroAvatars(response.data);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching astro avatars:', error);
                setLoading(false);
            }
        };

        fetchAstroAvatars();
    }, [apiUrl, token]);

    const handleStartChat = async (avatar) => {
        setSelectedAvatar(avatar);
        if (avatar.is_compatibility_check) {
            setProfileTitle(avatar.compatibility_title || 'Choose a Partner Profile');
            setProfileDescription(
                avatar.compatibility_description ||
                `Lovina, the love compatibility expert, needs details about both partners to offer the most accurate reading. 
                Please choose the second person’s profile below or add a new one.`
            );
            setProfileModal(true); // Show profile selection modal
        } else {
            // If no compatibility check is required, proceed to start chat
            try {
                const response = await axios.post(
                    `${apiUrl}/sessions`,
                    {
                        name: `Session with ${avatar.avatar_name}`,
                        astro_avatar_id: avatar.id,
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
                const newSession = response.data;
                navigate(`/chat/${newSession.id}`);
            } catch (error) {
                console.error('Error creating session:', error);
            }
        }
    };

    const handleProfileConfirm = async (profileId) => {
        setProfileModal(false);

        try {
            const response = await axios.post(
                `${apiUrl}/sessions`,
                {
                    name: `Session with ${selectedAvatar.avatar_name}`,
                    astro_avatar_id: selectedAvatar.id,
                    profile_id: profileId, // Pass selected profile_id
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            const newSession = response.data;
            navigate(`/chat/${newSession.id}`);
        } catch (error) {
            console.error('Error creating session:', error);
        }
    };

    return (
        <AstroAvatarsContext.Provider value={{ astroAvatars, loading, handleStartChat }}>
            {children}

            {/* Profile Selection Modal */}
            <Modal isOpen={profileModal} toggle={() => setProfileModal(false)}>
                <ModalHeader toggle={() => setProfileModal(false)} className="modal-title-small">
                    {profileTitle}
                </ModalHeader>
                <ModalBody>
                    <p className="mb-4 text-muted small-txt">
                        {profileDescription}
                    </p>
                    {profiles.length > 0 ? (
                        <>
                            <Row>
                                <Col md="12" className="mb-2">
                                    {profiles.map((profile) => (
                                    
                                        <div className="profile-card-wrap" key={profile.id}>
                                            <div className="astro-card-content">
                                                <h4>{profile.name}</h4>
                                                <div className="astro-skills"><b>DOB:</b> {profile.dob}</div>
                                                <div className="astro-skills"><b>City:</b> {profile.birth_city}</div>
                                            </div>
                                            <div className="astro-card-btn" onClick={() => handleProfileConfirm(profile.id)}>
                                                Select {profile.name}
                                            </div>
                                        </div>
                                    
                                    ))}
                                </Col>
                            </Row>
                            <div className="separate-div"><span>or</span></div>
                            <div className="start-session-btn" onClick={toggleAddProfile}>
                                <AddCircleOutlineIcon className="mr-2" /> Add Partner Profile
                            </div>
                        </>
                    ) : (
                        <div className="text-center">
                            <div className="start-session-btn" onClick={toggleAddProfile}>
                                <AddCircleOutlineIcon className="mr-2" /> Add Partner Profile
                            </div>
                        </div>
                    )}
                </ModalBody>
                
            </Modal>
        </AstroAvatarsContext.Provider>
    );
};

export default AstroAvatarsProvider;
